import _ from "lodash";
/*--external-- */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Button, Tag } from "@toddle-edu/ds-web";
import { EyeOutlined, EyeHiddenOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";

/**--internal-- */
import { generateRandomId, generateRandomNumber } from "Utils";
import KonvaCanvas from "UIComponents/KonvaCanvas";
import UIModal from "UIComponents/UIModal";

/**--relative--*/
import classes from "./DragAndDrop.scss";
import ElementContextMenu from "../ElementContextMenu";
import DragAndDropCanvasHeader from "../DragAndDropCanvasHeader";
import DropzoneWarnings from "../DropzoneWarnings";
import {
  getInitPropsForNewShape,
  stableOrderizeData,
  isDropzone,
  sanitizeQuestionData,
  getDragAndDropCanvasWidth,
} from "./DragAndDropUtils";
import {
  DRAG_AND_DROP_CATEGORY,
  DRAG_AND_DROP_ITEM_TYPE,
} from "./DragAndDropConstant";

/**
 *
 * @param {*} props
 * @returns Canvas Header(Add Items & Fullscreen) and Canvas
 */
const DragAndDrop = props => {
  const {
    t,
    mode,
    data = [],
    config = {},
    answersMapping = {},
    isFullscreen = false,
    onUpdateData = () => {},
    onToggleFullscreen = () => {},
    worksheetMode,
    isFromInternalTool,
  } = props;
  const dataRef = useRef([]);
  const submissionRef = useRef({});
  const [drawMode, setDrawMode] = useState({});
  const [viewSolution, setViewSolution] = useState(false);

  const fieldId = _.get(config, "fieldId", "");

  // * in draft mode, showing correct answer in MODE_ASSESSMENT_VIEW
  useEffect(() => {
    if (mode === "solution_view" || mode === "view") {
      setViewSolution(mode === "solution_view");
    }
  }, [mode]);

  useEffect(() => {
    submissionRef.current = _.isObject(answersMapping) ? answersMapping : {};
  }, [answersMapping]);

  /**
   * Add new item into data and redraw
   */
  const handleAddNewItem = useCallback(
    item => {
      const itemKeys = _.split(_.get(item, "key"), "#");
      const category = _.first(itemKeys);
      const type = _.get(itemKeys, "1", DRAG_AND_DROP_ITEM_TYPE.TEXT);
      const canvasId = `konva-canvas-container-id-${fieldId}-${mode}`;
      const payload = { t, type, item, category, canvasId };
      const newItem = getInitPropsForNewShape(payload);

      // * if dropzone start drawing on canvas
      if (isDropzone(category)) {
        setDrawMode({ type: type, isDrawing: true });
        return;
      }

      setDrawMode({});
      const updatedData = stableOrderizeData([...dataRef.current, newItem]);
      onUpdateData(updatedData);
    },
    [mode, fieldId]
  );

  /**
   * Update data on change in canvas
   */
  const handleItemsChange = useCallback(
    updatedItem => {
      const updateOnlyAnswer = _.get(updatedItem, "updateOnlyAnswer");

      if (_.has(updatedItem, "updateOnlyAnswer")) {
        delete updatedItem.updateOnlyAnswer;
      }

      dataRef.current = _.map(dataRef.current, item => {
        if (_.isEqual(_.get(updatedItem, "id"), _.get(item, "id"))) {
          return {
            ...(_.isObject(item) ? item : {}),
            ...(_.isObject(updatedItem) ? updatedItem : {}),
          };
        } else {
          return item;
        }
      });

      const updatedData = stableOrderizeData(dataRef.current);
      onUpdateData(
        updatedData,
        _.clone(submissionRef.current),
        updateOnlyAnswer
      );
    },
    [mode, onUpdateData]
  );

  /**
   * Handle if any draggable item is dropped inside dropzone
   */
  const handleItemDrop = useCallback(
    ({ draggedItemId, dropzoneId, action }) => {
      const updatedData = stableOrderizeData(dataRef.current);
      const submissionData = _.cloneDeep(submissionRef.current);
      _.forOwn(submissionData, mapping => _.pull(mapping, draggedItemId));
      if (action !== "REMOVE" && dropzoneId) {
        const existingMappings = _.get(submissionData, dropzoneId, []);
        existingMappings.push(draggedItemId);
        _.set(submissionData, dropzoneId, _.uniq(existingMappings));
        onUpdateData(updatedData, _.omitBy(submissionData, _.isEmpty));
      }

      submissionRef.current = _.omitBy(submissionData, _.isEmpty);
    },
    [mode]
  );

  /**
   * Remove item from data and redraw
   */
  const handleItemRemove = useCallback(
    id => {
      onUpdateData(_.filter(dataRef.current, item => _.get(item, "id") !== id));
    },
    [mode]
  );

  /**
   * On draw complete, add item into data and redraw
   */
  const handleDrawComplete = useCallback(
    payload => {
      let item = {
        id: generateRandomId(),
        type: _.get(drawMode, "type"),
        category: DRAG_AND_DROP_CATEGORY.DROPZONE,
      };
      if (item.type === DRAG_AND_DROP_ITEM_TYPE.POLYGON) {
        item.points = payload;
      } else if (
        item.type === DRAG_AND_DROP_ITEM_TYPE.ELLIPSE ||
        item.type === DRAG_AND_DROP_ITEM_TYPE.RECTANGLE
      ) {
        item = { ...item, ...payload };
      }

      setDrawMode({});
      const updatedData = stableOrderizeData([...dataRef.current, item]);
      setTimeout(() => onUpdateData(updatedData));
    },
    [drawMode]
  );

  /**
   * On duplicating element update id and add new element
   */
  const handleDuplicateItem = useCallback(
    id => {
      const item = _.find(dataRef.current, { id });
      if (!item) return;

      const newItem = _.cloneDeep(item);
      newItem.id = generateRandomId();
      if (_.has(newItem, "x")) newItem.x = generateRandomNumber(10, 100);
      if (_.has(newItem, "y")) newItem.y = generateRandomNumber(10, 100);
      if (_.has(newItem, "points")) {
        newItem.points = _.map(newItem.points, point => point + 40);
      }
      if (_.has(newItem, "correctElements")) {
        newItem.correctElements = [];
      }
      const updatedData = stableOrderizeData([...dataRef.current, newItem]);
      onUpdateData(updatedData);
    },
    [mode]
  );

  /**
   * Reset state if draw is cancelled
   */
  const handleDrawCancel = useCallback(() => {
    setDrawMode({});
  }, [mode]);

  /**
   * Order element on canvas - zIndex management
   */
  const handleElementOrder = useCallback(
    (elementId, orderType) => {
      const elements = stableOrderizeData(_.clone(dataRef.current || []));
      const index = _.findIndex(elements, { id: elementId });
      const category = _.get(elements, [index, "category"]);
      const elementsCount = _.get(_.countBy(elements, { category }), "true", 0);

      // * no ordering: if, no elements or elements is less than 2
      if (index < 0 || !_.isArray(elements) || elementsCount < 2) return;

      const minIndex = _.findIndex(elements, { category });
      const maxIndex = minIndex + elementsCount - 1;
      const newIndex = orderType === "DOWN" ? index - 1 : index + 1;

      if (newIndex < minIndex || newIndex > maxIndex) return;

      // * removing from current position
      const elementToMove = _.first(elements.splice(index, 1));
      if (!elementToMove) return;

      // * inserting element to new position
      elements.splice(newIndex, 0, elementToMove);
      onUpdateData(elements);
    },
    [mode]
  );

  /**
   * Show context menu for shapes in create mode
   */
  const elementContextMenu = useCallback(
    node => {
      return (
        <ElementContextMenu
          node={node}
          onRemove={handleItemRemove}
          onChange={handleItemsChange}
          onOrderElement={handleElementOrder}
        />
      );
    },
    [handleItemRemove, handleItemsChange, handleElementOrder]
  );

  /**
   * Show warning icons for no tagged answer dropzone
   */
  const showWarningIcons = useCallback(
    (data, mode, instance) => {
      if (mode !== "create") return null;
      return (
        <DropzoneWarnings
          data={data}
          instance={instance}
          onChange={handleItemsChange}
        />
      );
    },
    [JSON.stringify(data), handleAddNewItem]
  );

  /**
   * Memoized canvas component
   */
  const canvasComponent = useMemo(() => {
    dataRef.current = data || [];

    // * canvas width for different mode
    const canvasWidth = getDragAndDropCanvasWidth(isFullscreen);

    // * updating json data to required formate for canvas
    const canvasData = sanitizeQuestionData(_.cloneDeep(data), {
      mode,
      drawMode,
      viewSolution,
      allowInteraction: _.get(config, "allowInteraction"),
    });

    return (
      <KonvaCanvas
        mode={mode}
        key={fieldId}
        fieldId={fieldId}
        data={canvasData}
        width={canvasWidth}
        drawMode={drawMode}
        onDrop={handleItemDrop}
        onRemove={handleItemRemove}
        onChange={handleItemsChange}
        worksheetMode={worksheetMode}
        onDrawCancel={handleDrawCancel}
        onDuplicate={handleDuplicateItem}
        contextMenu={elementContextMenu}
        extraElements={showWarningIcons}
        onDrawComplete={handleDrawComplete}
        type={_.get(config, "fieldKey", "")}
        setAttributes={instance => {
          if (_.has(instance, "stage")) {
            instance?.stage.setAttr(
              "answersMapping",
              submissionRef.current || {}
            );
          }
        }}
        options={{
          viewSolution:
            viewSolution || !_.get(config, "showViewSolutionButton"),
          isSubmitted: _.get(config, "isSubmitted", false),
          allowInteraction: _.get(config, "allowInteraction", false),
        }}
      />
    );
  }, [
    mode,
    config,
    drawMode,
    viewSolution,
    isFullscreen,
    worksheetMode,
    JSON.stringify(data),
  ]);

  // * Return JSX for Full screen mode
  if (isFullscreen) {
    return (
      <UIModal
        isFullscreen
        isOpen={isFullscreen}
        modalContent={classes.modalContent}
        onRequestClose={onToggleFullscreen}
        testId="worksheet-dragAndDrop-closeModal-uiModal"
      >
        <DragAndDropCanvasHeader
          t={t}
          mode={mode}
          config={config}
          isFullscreen={isFullscreen}
          onAddNewItem={handleAddNewItem}
          onToggleFullscreen={onToggleFullscreen}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
        />
        <div className={classes.canvasContainer}>
          <div style={{ position: "relative" }}>{canvasComponent}</div>
        </div>
      </UIModal>
    );
  }

  const showViewSolutionTag =
    viewSolution && _.get(config, "showViewSolutionButton");
  const containerClassName = classNames(classes.container, {
    [classes.containerOverflowHidden]: mode !== "create",
    [classes.containerViewSolution]: showViewSolutionTag,
  });

  return (
    <>
      <div className={containerClassName}>
        <DragAndDropCanvasHeader
          t={t}
          mode={mode}
          config={config}
          onAddNewItem={handleAddNewItem}
          onToggleFullscreen={onToggleFullscreen}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
        />
        <div className={classes.canvasContainer}>{canvasComponent}</div>
        {showViewSolutionTag && (
          <div className={classes.viewSolutionTagWrapper}>
            <Tag
              _legacy_size="x-small"
              size="small"
              color="teal"
              dsVersion="2.0"
            >
              {t("worksheet:viewing_solution")}
            </Tag>
          </div>
        )}
      </div>
      {/* View solution will be visible for teacher evaluation mode to view correct answer mode */}
      {_.get(config, "showViewSolutionButton") && (
        <div className={classes.viewSolutionBtnWrapper}>
          <Button
            dsVersion="2.0"
            size="medium"
            variant="neutral"
            type="outlined"
            _legacy_size="small"
            _legacy_variant="outlined"
            icon={viewSolution ? <EyeHiddenOutlined /> : <EyeOutlined />}
            onClick={() => setViewSolution(prev => !prev)}
            testId="worksheet-dragAndDrop-hideViewSolution"
          >
            {t(
              viewSolution
                ? "worksheet:hide_solution"
                : "worksheet:view_solution"
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default DragAndDrop;

DragAndDrop.defaultProps = {
  data: [],
  t: () => "",
  mode: "preview",
  answersMapping: {},
  isFullscreen: false,
  onUpdateData: () => {},
  onToggleFullscreen: () => {},
};

DragAndDrop.propTypes = {
  data: PropTypes.array,
  t: PropTypes.func,
  mode: PropTypes.string,
  answersMapping: PropTypes.object,
  isFullscreen: PropTypes.bool,
  onUpdateData: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
};
